import React, { useState, useEffect } from 'react';
import { useDispatch, connect } from 'react-redux';
import { PlusIcon } from '@rsos/assets/icons';
import { BinIcon } from '@rsos/assets/icons';
import { Box, Button, Text } from '@rsos/components/capstone/base';
import { selectUserOrgByApp } from '@rsos/sinatra';
import { notifyError, notifyInfo } from '@rsos/utils/toastUtils';
import {
  fetchAllIntegrationCredentials,
  fetchIntegrationCredential,
  deleteIntegrationCredential,
} from '../actions';
import { fetchAllIntegrationCredentialsFailed } from '../actions/fetchAllIntegrationCredentials';
import { GENERIC_REQUEST_ERROR_MESSAGE } from '../constants/errorMessages';
import InboundTable from './InboundTable';
import {
  IntegrationManagementWrapper,
  Title,
  Subtitle,
  IconWrapper,
  IMTitleContainer,
} from './Main.styles';
import AddCredentialModal from './modals/AddCredentialModal';
import DeleteCredentialModal from './modals/DeleteCredentialModal';
import EditCredentialModal from './modals/EditCredentialModal';
import { partnerSchema } from './partnerSchema';

const Inbound = ({ sinatra, data, psaps }) => {
  const [isFetching, setIsFetching] = useState(true);
  const [addCredentialModalIsActive, setAddCredentialModalIsActive] =
    useState(false);
  const [editCredentialModalIsActive, setEditCredentialModalIsActive] =
    useState(false);
  const [deleteCredentialModalIsActive, setDeleteCredentialModalIsActive] =
    useState(false);
  const [selectedDeleteCredential, setSelectedDeleteCredential] =
    useState(null);
  const [selectedEditCredential, setSelectedEditCredential] = useState(null);
  const dispatch = useDispatch();
  const { application } = sinatra.user.currentRole;

  const org = selectUserOrgByApp(sinatra, application);
  const orgID = org.id;

  const fetchAllCreds = async () => {
    await dispatch(fetchAllIntegrationCredentials(orgID)).then(res => {
      if (!res?.payload?.credentials) {
        dispatch(fetchAllIntegrationCredentialsFailed(res.status));
      }
    });
  };

  useEffect(() => {
    fetchAllCreds().then(() => setIsFetching(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleAddCredentialModal = () => {
    setAddCredentialModalIsActive(prev => !prev);
  };
  const toggleEditCredentialModal = () => {
    setEditCredentialModalIsActive(prev => !prev);
  };
  const toggleDeleteCredentialModal = () => {
    setDeleteCredentialModalIsActive(prev => !prev);
  };

  const fetchCredential = cred => {
    dispatch(fetchIntegrationCredential(orgID, cred.internal_id)).then(res => {
      if (res.client_id) {
        setSelectedEditCredential(res);
        toggleEditCredentialModal();
      } else {
        notifyError(GENERIC_REQUEST_ERROR_MESSAGE);
      }
    });
  };

  const handleDeleteCredential = () => {
    dispatch(deleteIntegrationCredential(orgID, selectedDeleteCredential)).then(
      res => {
        if (!res.ok) {
          notifyError(GENERIC_REQUEST_ERROR_MESSAGE);
        } else {
          toggleDeleteCredentialModal();
          const options = {
            message: `${selectedDeleteCredential.credential_name} Department has been deleted.`,
            customIcon: BinIcon,
          };
          notifyInfo(options);
        }
      },
    );
  };

  return (
    <IntegrationManagementWrapper>
      <IMTitleContainer>
        <Title>Manage Sources</Title>
        <Subtitle>for Inbound Data</Subtitle>
      </IMTitleContainer>
      <Box my={20} flow={10}>
        <Text size="normal" color="primaryText" inline>
          <Text size="normal" fontWeight="bold" color="primaryText" inline>
            Control what RapidSOS data partners your ECC can access.
          </Text>
          &nbsp;Here you can add, view, edit, delete the credentials required to
          access RapidSOS partner data. Note that only some partners require
          access credentials.
        </Text>
        <Box horizontal flow={7} m={'16px 0'}>
          <Button
            size="med"
            btnType="primary"
            onClick={toggleAddCredentialModal}
            m={'0 9px 0 0'}
          >
            <IconWrapper px={1}>
              <PlusIcon height={13} width={13} />
            </IconWrapper>
            Add Credential
          </Button>
          <Box width={233} justify="center">
            <Text size="normal" color="secondaryText" inline width={'233px'}>
              *Please handle all credentials securely and with care.
            </Text>
          </Box>
        </Box>
        <InboundTable
          isFetching={isFetching}
          credentials={data?.credentials}
          fetchCredential={fetchCredential}
          toggleAddCredentialModal={toggleAddCredentialModal}
          toggleDeleteCredentialModal={toggleDeleteCredentialModal}
          setSelectedDeleteCredential={setSelectedDeleteCredential}
        />

        <AddCredentialModal
          orgID={orgID}
          psaps={psaps}
          dataSource={partnerSchema}
          onClose={toggleAddCredentialModal}
          isOpened={addCredentialModalIsActive}
        />
        <EditCredentialModal
          orgID={orgID}
          onClose={toggleEditCredentialModal}
          isOpened={editCredentialModalIsActive}
          selectedEditCredential={selectedEditCredential}
        />
        <DeleteCredentialModal
          orgID={orgID}
          onClose={toggleDeleteCredentialModal}
          isOpened={deleteCredentialModalIsActive}
          handleDeleteCredential={handleDeleteCredential}
        />
      </Box>
    </IntegrationManagementWrapper>
  );
};

const mapStateToProps = state => {
  return {
    sinatra: state.sinatra,
    data: state.integrations.data,
    psaps: state.psaps,
  };
};

export default connect(mapStateToProps, null)(Inbound);
